import React, { useState, useRef } from 'react';
import {
    Book, Search, Globe, Terminal,
    Lock, Info, X,
    Shuffle, Database, Shield, Zap, CheckCircle, Activity, Server, ShieldCheck, ArrowRight, Monitor, Wifi, Key, ChevronDown, Sun, Moon, ShieldAlert
} from 'lucide-react';
import './EnterpriseDMS.css';
import { ParticleCard, GlobalSpotlight } from '../components/MagicBento';

const DocumentationPage = ({ isFullPage = true, onClose, theme: themeProp, toggleTheme: toggleThemeProp }) => {
    const getInitialTheme = () => themeProp || localStorage.getItem('theme') || 'dark';
    const [localTheme, setLocalTheme] = React.useState(getInitialTheme);
    const theme = themeProp !== undefined ? themeProp : localTheme;
    const toggleTheme = toggleThemeProp || (() => setLocalTheme(t => t === 'dark' ? 'light' : 'dark'));
    const [activeSection, setActiveSection] = useState('overview');
    const [expandedNav, setExpandedNav] = useState({
        source: true,
        connectivity: true,
        migration: true
    });
    const infoGridRef = useRef(null);
    const techGridRef = useRef(null);
    const providerGridRef = useRef(null);

    const toggleNav = (section) => {
        setExpandedNav(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'overview':
                return (
                    <div className="doc-section fade-in">
                        <div className="hero-section">
                            <div className="hero-badge">Enterprise Documentation</div>
                            <h1 className="doc-h1 gradient-text">System Overview</h1>
                            <p className="doc-p large-p">Enterprise Database Migration Service (DMS) is a high-performance, serverless platform designed to migrate your data securely and with minimal downtime.</p>
                        </div>

                        <div className="info-grid" ref={infoGridRef} style={{ position: 'relative' }}>
                            <GlobalSpotlight gridRef={infoGridRef} cardSelector=".info-card" />
                            <ParticleCard className="info-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <div className="info-icon purple"><Shuffle size={20} /></div>
                                <div className="info-body">
                                    <h5>Zero-Downtime Migration</h5>
                                    <p>Combine initial full-load snapshots with continuous CDC replication to keep your destination in sync until cutover.</p>
                                </div>
                            </ParticleCard>
                            <ParticleCard className="info-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <div className="info-icon blue"><ShieldCheck size={20} /></div>
                                <div className="info-body">
                                    <h5>Enterprise Security</h5>
                                    <p>Secure your data in transit with managed SSH tunnels, IP allowlisting, or automated <strong>VPN Bridges</strong>.</p>
                                </div>
                            </ParticleCard>
                            <ParticleCard className="info-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <div className="info-icon amber"><Search size={20} /></div>
                                <div className="info-body">
                                    <h5>Intelligent Discovery</h5>
                                    <p>The built-in discovery engine automatically scans your source instances to identify schemas and tables eligible for migration.</p>
                                </div>
                            </ParticleCard>
                            <ParticleCard className="info-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <div className="info-icon emerald"><Activity size={20} /></div>
                                <div className="info-body">
                                    <h5>Real-time Telemetry</h5>
                                    <p>Monitor every phase of your migration with millisecond-level precision, including throughput, latency, and row counts.</p>
                                </div>
                            </ParticleCard>
                        </div>

                        <div className="doc-split-layout">
                            <div className="split-left">
                                <h2 className="doc-h2" style={{ marginTop: '0' }}>Core Benefits</h2>
                                <ul className="premium-list">
                                    <li>
                                        <div className="list-dot" />
                                        <div><strong>Simplified Workflow:</strong> A guided 6-step wizard takes you from connection profiling to final cutover.</div>
                                    </li>
                                    <li>
                                        <div className="list-dot" />
                                        <div><strong>High Fidelity:</strong> Ensures data integrity across homogeneous and heterogeneous migrations.</div>
                                    </li>
                                    <li>
                                        <div className="list-dot" />
                                        <div><strong>Cloud Native:</strong> Leverages modern infrastructure (Kafka, Debezium, and Terraform) for scale.</div>
                                    </li>
                                    <li>
                                        <div className="list-dot" />
                                        <div><strong>Pre-flight Validation:</strong> Deep diagnostic checks catch configuration issues before you start.</div>
                                    </li>
                                </ul>
                            </div>
                            <div className="split-right">
                                <div className="doc-note-alt">
                                    <Info size={20} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                                    <div>
                                        <strong>Core Requirement</strong>
                                        Every migration job requires at least one Source and one Destination Connection. You can pre-configure these in the Connections page.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'get-setup':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Get set up</h1>
                        <p className="doc-p large-p">Enterprise Database Migration Service provides high-fidelity, continuous data replication from various source databases with minimal downtime.</p>

                        <h2 className="doc-h2">Prerequisites</h2>
                        <ul className="doc-list">
                            <li>At least one valid <strong>Source Connection</strong> configured and tested.</li>
                            <li>At least one valid <strong>Destination Connection</strong> configured and tested.</li>
                            <li>Network access to both your source and destination databases.</li>
                            <li>Administrative privileges on the source database to configure replication parameters.</li>
                        </ul>

                        <div className="doc-note accent">
                            <CheckCircle size={20} color="#10b981" style={{ marginRight: '12px', flexShrink: 0 }} />
                            <div>
                                <strong>Pro Tip:</strong> Before starting, review the <strong>Configure your source</strong> guide to ensure logical replication is enabled on your source instance.
                            </div>
                        </div>
                    </div>
                );

            case 'configure-source':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Configure your source</h1>
                        <p className="doc-p">Before starting a migration, your source database instance must be prepared to handle high-fidelity replication. This typically involves enabling logical replication and installing the <code>pglogical</code> extension.</p>

                        <div className="doc-note">
                            <Zap size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Quick Setup Tip:</strong> For all providers (AWS, GCP, Azure, or On-prem), our <strong>Pre-flight Validation (Step 6)</strong> can automatically generate the necessary configuration scripts for you.
                            </div>
                        </div>

                        <h2 className="doc-h2">Select your provider</h2>
                        <div className="provider-grid" ref={providerGridRef} style={{ position: 'relative' }}>
                            <GlobalSpotlight gridRef={providerGridRef} cardSelector=".provider-card" />
                            <ParticleCard as="div" className={`provider-card ${activeSection === 'source-onprem' ? 'active' : ''}`} onClick={() => setActiveSection('source-onprem')} enableTilt={true} enableMagnetism={false} clickEffect={true}>
                                <div className="provider-icon-box purple">
                                    <Server size={22} />
                                </div>
                                <div className="provider-info">
                                    <h6>On-premise / VM</h6>
                                    <p>Self-managed PostgreSQL instances on Linux/Windows VMs.</p>
                                </div>
                                <ArrowRight className="provider-arrow" size={16} />
                            </ParticleCard>
                            <ParticleCard as="div" className={`provider-card ${activeSection === 'source-rds' ? 'active' : ''}`} onClick={() => setActiveSection('source-rds')} enableTilt={true} enableMagnetism={false} clickEffect={true}>
                                <div className="provider-icon-box blue">
                                    <Database size={22} />
                                </div>
                                <div className="provider-info">
                                    <h6>Amazon RDS</h6>
                                    <p>Managed AWS RDS and Aurora PostgreSQL instances.</p>
                                </div>
                                <ArrowRight className="provider-arrow" size={16} />
                            </ParticleCard>
                            <ParticleCard as="div" className={`provider-card ${activeSection === 'source-cloudsql' ? 'active' : ''}`} onClick={() => setActiveSection('source-cloudsql')} enableTilt={true} enableMagnetism={false} clickEffect={true}>
                                <div className="provider-icon-box amber">
                                    <Globe size={22} />
                                </div>
                                <div className="provider-info">
                                    <h6>Google Cloud SQL</h6>
                                    <p>Managed GCP Cloud SQL for PostgreSQL.</p>
                                </div>
                                <ArrowRight className="provider-arrow" size={16} />
                            </ParticleCard>
                            <ParticleCard as="div" className={`provider-card ${activeSection === 'source-azure' ? 'active' : ''}`} onClick={() => setActiveSection('source-azure')} enableTilt={true} enableMagnetism={false} clickEffect={true}>
                                <div className="provider-icon-box emerald">
                                    <Monitor size={22} />
                                </div>
                                <div className="provider-info">
                                    <h6>Azure Database</h6>
                                    <p>Azure Database for PostgreSQL Flexible Server.</p>
                                </div>
                                <ArrowRight className="provider-arrow" size={16} />
                            </ParticleCard>
                        </div>
                    </div>
                );

            case 'source-onprem':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">PostgreSQL (On-premise or VM)</h1>
                        <p className="doc-p">Configuration for self-managed instances requires shell access to the database server.</p>

                        <div className="doc-note">
                            <Zap size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Quick Setup:</strong> Use Step 6 in the Migration Wizard to generate a one-click shell script for your VM.
                            </div>
                        </div>

                        <h2 className="doc-h2">Configuration Steps</h2>
                        <ol className="doc-list-numbered">
                            <li><strong>[OS Level / Shell]</strong> Install <code>pglogical</code>:
                                <div className="code-block"><pre>apt-get install postgresql-xx-pglogical</pre></div>
                            </li>
                            <li><strong>[Database Level]</strong> Enable replication in SQL:
                                <div className="code-block"><pre>ALTER SYSTEM SET shared_preload_libraries = 'pglogical';{'\n'}ALTER SYSTEM SET wal_level = 'logical';</pre></div>
                            </li>
                            <li><strong>[OS Level]</strong> Restart the service:
                                <div className="code-block"><pre>sudo service postgresql restart</pre></div>
                            </li>
                            <li><strong>[Filesystem Level]</strong> Alternatively, edit <code>postgresql.conf</code>:
                                <div className="code-block"><pre>shared_preload_libraries = 'pglogical'{'\n'}wal_level = 'logical'</pre></div>
                            </li>
                        </ol>
                    </div>
                );

            case 'source-rds':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Amazon RDS / Aurora</h1>
                        <p className="doc-p">For AWS-managed instances, configuration is handled via Parameter Groups.</p>

                        <div className="doc-note">
                            <Zap size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Quick Setup:</strong> Step 6 will identify your missing RDS parameters and provide the exact values for your Parameter Group.
                            </div>
                        </div>

                        <h2 className="doc-h2">Setup Steps</h2>
                        <ol className="doc-list-numbered">
                            <li><strong>[AWS Console]</strong> Create/Edit a <strong>Parameter Group</strong>.</li>
                            <li>Set <code>rds.logical_replication</code> = <code>1</code>.</li>
                            <li>Set <code>shared_preload_libraries</code> = <code>pglogical</code>.</li>
                            <li><strong>[AWS Console]</strong> Reboot the instance to apply the group.</li>
                            <li><strong>[SQL Client]</strong> Run:
                                <div className="code-block"><pre>CREATE EXTENSION pglogical;</pre></div>
                            </li>
                        </ol>
                    </div>
                );

            case 'source-cloudsql':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Google Cloud SQL</h1>
                        <p className="doc-p">GCP handles replication settings via Database Flags.</p>

                        <div className="doc-note">
                            <Zap size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Quick Setup:</strong> Our wizard will check these flags and confirm readiness before you start.
                            </div>
                        </div>

                        <h2 className="doc-h2">Setup Steps</h2>
                        <ol className="doc-list-numbered">
                            <li><strong>[GCP Console]</strong> Set <code>cloudsql.logical_decoding</code> = <code>on</code>.</li>
                            <li><strong>[GCP Console]</strong> Set <code>cloudsql.enable_pglogical</code> = <code>on</code>.</li>
                            <li><strong>[SQL Client]</strong> Run:
                                <div className="code-block"><pre>CREATE EXTENSION pglogical;</pre></div>
                            </li>
                        </ol>
                    </div>
                );

            case 'source-azure':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Azure Database (MySQL & PostgreSQL)</h1>

                        <div className="doc-note">
                            <Zap size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Quick Setup:</strong> Step 6 automatically validates your Azure Server Parameters and provides engine-specific fix guides.
                            </div>
                        </div>

                        <h2 className="doc-h2">Azure Database for MySQL Setup</h2>
                        <ol className="doc-list-numbered">
                            <li><strong>[Azure Portal]</strong> Open your <strong>Azure Database for MySQL Flexible Server</strong> instance.</li>
                            <li>Go to <strong>Server parameters</strong>.</li>
                            <li>Search for <code>binlog_format</code> and set value to <code>ROW</code>.</li>
                            <li>Search for <code>enforce_gtid_consistency</code> and set value to <code>ON</code>.</li>
                            <li>Click <strong>Save</strong> to apply changes.</li>
                        </ol>

                        <h2 className="doc-h2" style={{ marginTop: '2rem' }}>Azure Database for PostgreSQL Setup</h2>
                        <ol className="doc-list-numbered">
                            <li><strong>[Azure Portal]</strong> Open your <strong>Azure Database for PostgreSQL Flexible Server</strong> instance.</li>
                            <li>Go to <strong>Server parameters</strong>.</li>
                            <li>Search for <code>wal_level</code> and set value to <code>logical</code>.</li>
                            <li>Set <code>azure.extensions</code> to include <code>pglogical</code> (if using pglogical).</li>
                            <li>Click <strong>Save</strong> and restart the server instance.</li>
                        </ol>
                    </div>
                );

            case 'create-connection':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Create a source connection</h1>
                        <p className="doc-p large-p">Connections store information about the source and destination databases, including their IP addresses, ports, and credentials.</p>

                        <ol className="doc-list-numbered">
                            <li>Go to the <strong className="doc-link" onClick={() => { if (onClose) onClose(); window.location.hash = '#/connections'; }}>Connections</strong> page.</li>
                            <li>Click <strong>Create connection</strong> and select your database engine.</li>
                            <li><strong>Select Connection Type:</strong> Choose <strong>Source</strong>.</li>
                            <li>In the <strong>Connection information</strong> section, specify the connectivity method:
                                <ul className="doc-list" style={{ marginTop: '1rem' }}>
                                    <li><strong className="doc-link" onClick={() => setActiveSection('conn-ip')}>Public Internet</strong>: Uses IP allowlisting.</li>
                                    <li><strong className="doc-link" onClick={() => setActiveSection('conn-ssh')}>Managed SSH</strong>: Automated secure tunnel.</li>
                                    <li><strong className="doc-link" onClick={() => setActiveSection('conn-vpn')}>VPN Bridge</strong>: Enterprise cross-cloud connectivity.</li>
                                </ul>
                            </li>
                            <li>Enter the hostname/IP, port, and credentials.</li>
                            <li>Click <strong>Create</strong> to save.</li>
                        </ol>
                    </div>
                );

            case 'create-dest-connection':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Create a destination connection</h1>
                        <p className="doc-p large-p">A destination connection represents the target database instance where your data will be migrated.</p>

                        <div className="doc-note">
                            <Info size={20} color="#3b82f6" style={{ marginRight: '12px', flexShrink: 0 }} />
                            <div>
                                <strong>Requirement:</strong> Target engine must match the source engine (e.g., PostgreSQL to PostgreSQL).
                            </div>
                        </div>

                        <ol className="doc-list-numbered">
                            <li>Go to the <strong>Connections</strong> page and click <strong>Create connection</strong>.</li>
                            <li><strong>Select Connection Type:</strong> Choose <strong>Destination</strong>.</li>
                            <li>Enter the connection details for your Cloud SQL or RDS target.</li>
                            <li>Ensure the user has <code>CREATE</code> and <code>INSERT</code> privileges.</li>
                            <li>Click <strong>Create</strong> to save.</li>
                        </ol>
                    </div>
                );

            case 'conn-overview':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Connectivity Overview</h1>
                        <p className="doc-p large-p">Choose the connectivity method that best fits your security requirements and network architecture.</p>

                        <div className="technical-grid" ref={techGridRef} style={{ position: 'relative' }}>
                            <GlobalSpotlight gridRef={techGridRef} cardSelector=".tech-card" />
                            <ParticleCard className="tech-card premium-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <Globe size={24} color="#3b82f6" />
                                <h5>IP Allowlisting</h5>
                                <p>Simplest method for public databases. Restrict access to our dedicated egress IP.</p>
                            </ParticleCard>
                            <ParticleCard className="tech-card premium-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <Terminal size={24} color="var(--portal-accent)" />
                                <h5>Managed SSH</h5>
                                <p>Automated secure access to private databases via a bastion host.</p>
                            </ParticleCard>
                            <ParticleCard className="tech-card premium-card" enableTilt={true} enableMagnetism={false} clickEffect={false}>
                                <Shield size={24} color="#10b981" />
                                <h5>VPN Bridge</h5>
                                <p>High-speed, encrypted tunnel for cross-cloud or on-premise networks.</p>
                            </ParticleCard>
                        </div>
                    </div>
                );

            case 'conn-ip':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Using IP allowlists</h1>
                        <p className="doc-p">If your database is publicly accessible, you can restrict traffic to our platform's static IP.</p>

                        <h2 className="doc-h2">Your Dedicated Egress IP</h2>
                        <div className="ip-display-card">
                            <div className="ip-value">34.14.133.97</div>
                        </div>

                        <div className="doc-note">
                            <ShieldAlert size={24} color="#3b82f6" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Security Tip:</strong> Avoid using <code>0.0.0.0/0</code> for production. Always restrict access to the specific IP shown above.
                            </div>
                        </div>

                        <h2 className="doc-h2">Provider Examples</h2>
                        <ul className="doc-list">
                            <li><strong>AWS RDS:</strong> Add Inbound Rule (Type: PostgreSQL, Source: <code>34.14.133.97/32</code>).</li>
                            <li><strong>GCP Cloud SQL:</strong> Add Network (IP: <code>34.14.133.97</code>).</li>
                            <li><strong>Azure Database:</strong> Add Client IP (Address: <code>34.14.133.97</code>).</li>
                        </ul>
                    </div>
                );

            case 'conn-ssh':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Using Managed SSH</h1>
                        <p className="doc-p large-p">Managed SSH allows secure access to private databases by routing traffic through a bastion host without manual commands.</p>

                        <div className="doc-note">
                            <ShieldCheck size={24} color="var(--portal-accent)" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Automated Lifecycle:</strong> The platform acts as the SSH client and manages the tunnel during migration.
                            </div>
                        </div>

                        <h2 className="doc-h2">Setup Steps</h2>
                        <ol className="doc-list-numbered">
                            <li>Select <strong>Managed SSH</strong> in the connection modal.</li>
                            <li>Enter the **Bastion Host** (Public IP/Hostname) and **SSH Username**.</li>
                            <li>Paste your **SSH Private Key (PEM)** into the field.</li>
                            <li>Click **Test Connection** to verify the tunnel and database reachability.</li>
                        </ol>
                    </div>
                );

            case 'conn-vpn':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Using VPN Bridge</h1>
                        <p className="doc-p large-p">The automated VPN Bridge creates a direct, encrypted site-to-site tunnel between cloud providers using HA VPN (GCP) and Site-to-Site VPN (AWS).</p>

                        <h2 className="doc-h2">Setup Workflow</h2>
                        <ol className="doc-list-numbered">
                            <li>Configure your <strong className="doc-link" onClick={() => setActiveSection('creds-mgmt')}>Cloud Credentials</strong>.</li>
                            <li>Go to the <strong>VPN Bridge</strong> tab and click <strong>Initialize VPN Bridge</strong>.</li>
                            <li>Wait for the BGP status to turn <strong>Green (Established)</strong>.</li>
                            <li>Select <strong>VPN Bridge</strong> as the connectivity method for your connection profiles.</li>
                        </ol>

                        <div className="doc-note accent" style={{ marginTop: '3rem' }}>
                            <ShieldAlert size={24} color="#f59e0b" style={{ flexShrink: 0 }} />
                            <div>
                                <strong>Critical: AWS Post-Provisioning Checklist</strong>
                                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>If your Terraform Apply finishes but the connection test fails, check these two items in the AWS Console:</p>
                                <ul style={{ fontSize: '0.85rem', marginTop: '0.5rem', listStyle: 'disc', paddingLeft: '1.5rem' }}>
                                    <li style={{ marginBottom: '0.5rem' }}><strong>Route Propagation:</strong> If your RDS is in a private subnet with a custom route table, you must manually enable <strong>Route Propagation</strong> for the VPN Gateway in that specific table.</li>
                                    <li><strong>Security Groups:</strong> Manually attach the <code>dms-migration-bridge-allow-gcp</code> security group to your RDS instance.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                );

            case 'creds-mgmt':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Managing Credentials</h1>
                        <p className="doc-p large-p">Provide cloud credentials to automate infrastructure deployment like VPN Bridges.</p>

                        <h2 className="doc-h2">Steps</h2>
                        <ol className="doc-list-numbered">
                            <li>Click on your <strong>Profile Dropdown &gt; Credentials</strong>.</li>
                            <li>Click <strong>Add New Credential</strong>.</li>
                            <li>Select your provider (AWS, GCP, Azure) and paste the keys/JSON.</li>
                            <li>Click <strong>Save</strong>.</li>
                        </ol>

                        <div className="doc-note accent">
                            <Lock size={20} color="#10b981" style={{ marginRight: '12px', flexShrink: 0 }} />
                            <div>
                                <strong>Encrypted Storage:</strong> Credentials are encrypted with AES-256 and never stored in plain text.
                            </div>
                        </div>
                    </div>
                );


            case 'mig-overview':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">Migration Job Overview</h1>
                        <p className="doc-p large-p">A migration job orchestrates the movement of data from source to destination.</p>

                        <div className="tech-card premium-card" style={{ marginBottom: '1.5rem' }}>
                            <h5>Continuous Replication (CDC)</h5>
                            <p>Best for production. Performs a full dump and then streams changes until cutover.</p>
                        </div>
                        <div className="tech-card premium-card">
                            <h5>One-time Load</h5>
                            <p>Best for testing or small datasets. Performs a single snapshot and finishes.</p>
                        </div>
                    </div>
                );

            case 'mig-new':
            case 'mig-existing':
                return (
                    <div className="doc-section fade-in">
                        <h1 className="doc-h1">{activeSection === 'mig-new' ? 'New Destination Migration' : 'Existing Destination Migration'}</h1>
                        <p className="doc-p">Follow the 6-step wizard to configure your migration.</p>

                        <ol className="doc-list-numbered">
                            <li><strong>Step 1: Configuration.</strong> Set the name and type.</li>
                            <li><strong>Step 2 & 3: Profiles.</strong> Select your Source and Destination connections.</li>
                            <li><strong>Step 4: Databases.</strong> Select schemas and tables.</li>
                            <li><strong>Step 5: Settings.</strong> Tune performance (workers, buffer size).</li>
                            <li><strong>Step 6: Validation.</strong> Fix any red flags and click **Start Migration**.</li>
                        </ol>
                    </div>
                );

            default:
                return (
                    <div className="doc-section">
                        <h1 className="doc-h1">Documentation</h1>
                        <p className="doc-p">Select a topic from the sidebar to begin.</p>
                    </div>
                );
        }
    };

    return (
        <div className={`documentation-portal ${isFullPage ? 'full-page' : ''} ${theme === 'light' ? 'light-mode' : 'dark-mode'}`}>
            <style>{`
                .documentation-portal {
                    /* Premium Off-White Palette */
                    --portal-bg: ${theme === 'dark' ? '#0b0e14' : '#f9fafb'};
                    --portal-sidebar: ${theme === 'dark' ? '#0f1219' : '#ffffff'};
                    --portal-border: ${theme === 'dark' ? '#1e2533' : '#f3f4f6'};
                    --portal-text: ${theme === 'dark' ? '#f8fafc' : '#0f172a'};
                    --text-main: var(--portal-text); /* Fix for global overrides */
                    --portal-text-muted: ${theme === 'dark' ? '#94a3b8' : '#4b5563'};
                    --portal-accent: #6366f1;
                    --portal-accent-soft: ${theme === 'dark' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.05)'};
                    --portal-card: ${theme === 'dark' ? '#161b26' : '#ffffff'};
                    --portal-code-bg: ${theme === 'dark' ? '#07090d' : '#1e1b4b'};
                    --portal-code-text: #f8fafc;
                    --portal-nav-hover: ${theme === 'dark' ? '#1e2533' : '#f9fafb'};
                    --portal-shadow: ${theme === 'dark' ? '0 10px 30px rgba(0,0,0,0.6)' : '0 10px 40px rgba(0,0,0,0.03)'};
                    --portal-main-gradient: ${theme === 'dark' ? 'radial-gradient(circle at 50% -20%, #1e1b4b 0%, #0b0e14 100%)' : 'none'};
                    
                    display: flex; height: 100vh; background: var(--portal-bg); color: var(--portal-text);
                    background-image: var(--portal-main-gradient);
                    font-family: 'Inter', -apple-system, sans-serif;
                    overflow: hidden;
                }
                .full-page { width: 100vw; position: fixed; top: 0; left: 0; z-index: 1000; }
                
                .doc-sidebar {
                    width: 280px; border-right: 1px solid var(--portal-border);
                    display: flex; flex-direction: column; 
                    background: var(--portal-sidebar);
                    z-index: 10;
                    height: 100vh;
                }
                
                .doc-sidebar-header { padding: 1.5rem; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--portal-border); }
                .doc-logo { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 1.1rem; color: var(--portal-text); }
                .logo-icon-box { 
                    width: 32px; height: 32px; background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    border-radius: 8px; display: flex; align-items: center; justify-content: center;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
                }

                .doc-nav { flex: 1; overflow-y: auto; padding: 1rem 0; }
                .doc-nav::-webkit-scrollbar { width: 4px; }
                .doc-nav::-webkit-scrollbar-thumb { background: var(--portal-border); border-radius: 10px; }

                .nav-item {
                    padding: 0.75rem 1rem; margin: 0.1rem 0.75rem; border-radius: 8px;
                    display: flex; align-items: center; justify-content: space-between; cursor: pointer;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    color: var(--portal-text-muted); font-weight: 500; font-size: 0.9rem;
                }
                .nav-item:hover { background: var(--portal-nav-hover); color: var(--portal-text); transform: translateX(3px); }
                .nav-item.active { background: var(--portal-accent-soft) !important; color: var(--portal-accent) !important; font-weight: 600 !important; border: none !important; box-shadow: none !important; }
                .nav-item::before { display: none !important; }

                .doc-nav-item-content { display: flex; align-items: center; gap: 10px; }

                .nav-sub-items { overflow: hidden; transition: max-height 0.3s ease; }
                .nav-sub-items.collapsed { max-height: 0; }
                .nav-sub-items.expanded { max-height: 1000px; }

                .sub-nav-item {
                    padding: 0.5rem 1rem 0.5rem 2.75rem; border-radius: 6px; margin: 0.1rem 0.75rem;
                    color: var(--portal-text-muted); font-size: 0.85rem; cursor: pointer; transition: all 0.2s;
                }
                .sub-nav-item:hover { color: var(--portal-text); background: var(--portal-nav-hover); }
                .sub-nav-item.active { color: var(--portal-accent); font-weight: 600; }

                .doc-main-content { flex: 1; overflow-y: auto; padding: 3rem 4rem; scroll-behavior: smooth; }
                .doc-main-content::-webkit-scrollbar { width: 8px; }
                .doc-main-content::-webkit-scrollbar-thumb { background: var(--portal-border); border-radius: 10px; }

                /* Hero & Space Optimization */
                .hero-section { margin-bottom: 3rem; }
                .hero-badge {
                    display: inline-block; padding: 4px 12px; border-radius: 20px;
                    background: var(--portal-accent-soft); color: var(--portal-accent);
                    font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
                    margin-bottom: 1rem; border: 1px solid rgba(99, 102, 241, 0.2);
                }

                .gradient-text { 
                    background: ${theme === 'dark' ? 'linear-gradient(to right, #ffffff, #94a3b8)' : 'linear-gradient(to right, #0f172a, #475569)'}; 
                    -webkit-background-clip: text; -webkit-text-fill-color: transparent; 
                    font-weight: 800;
                }
                .doc-h1 { font-size: 2.75rem; margin-bottom: 1rem; letter-spacing: -0.02em; font-weight: 800; }
                .doc-h2 { font-size: 1.85rem; font-weight: 700; margin-top: 3rem; margin-bottom: 1.25rem; border-bottom: 1px solid var(--portal-border); padding-bottom: 0.75rem; }
                .doc-p { line-height: 1.7; color: var(--portal-text-muted); margin-bottom: 1.25rem; font-size: 1.05rem; }
                .doc-p.large-p { font-size: 1.2rem; line-height: 1.8; margin-bottom: 2rem; max-width: 800px; }

                /* Info Grid (2x2 Balanced) */
                .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.25rem; margin-bottom: 3rem; }
                .info-card {
                    background: var(--portal-card); border: 1px solid var(--portal-border);
                    border-radius: 16px; padding: 1.5rem; display: flex; gap: 1.25rem;
                    box-shadow: var(--portal-shadow); transition: all 0.3s;
                }
                .info-card:hover { transform: translateY(-3px); border-color: var(--portal-accent); }
                .info-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .info-body h5 { margin: 0 0 4px; font-size: 1rem; font-weight: 700; color: var(--portal-text); }
                .info-body p { margin: 0; font-size: 0.85rem; color: var(--portal-text-muted); line-height: 1.5; }

                /* Split Layout for Efficient Space */
                .doc-split-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 3rem; margin-top: 3rem; align-items: start; }
                .premium-list { list-style: none; padding: 0; margin: 0; }
                .premium-list li { display: flex; gap: 1rem; margin-bottom: 1.25rem; align-items: flex-start; color: var(--portal-text-muted); font-size: 0.95rem; line-height: 1.6; }
                .list-dot { width: 8px; height: 8px; background: var(--portal-accent); border-radius: 50%; margin-top: 8px; flex-shrink: 0; box-shadow: 0 0 10px var(--portal-accent); }

                .doc-note-alt {
                    background: var(--portal-sidebar); border: 1px solid var(--portal-border);
                    padding: 1.5rem; border-radius: 12px; display: flex; gap: 1rem;
                    box-shadow: var(--portal-shadow); position: sticky; top: 20px;
                }
                .doc-note-alt strong { display: block; color: var(--portal-text); margin-bottom: 0.5rem; font-size: 1rem; }
                .doc-note-alt div { font-size: 0.85rem; color: var(--portal-text-muted); line-height: 1.6; }

                /* Provider Grid Styling */
                .provider-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.25rem; margin: 2rem 0; }
                .provider-card {
                    background: var(--portal-card); border: 1px solid var(--portal-border);
                    border-radius: 16px; padding: 1.5rem; display: flex; align-items: center; gap: 1.25rem;
                    cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative; overflow: hidden;
                    box-shadow: var(--portal-shadow);
                }
                .provider-card:hover { transform: translateY(-4px); border-color: var(--portal-accent); box-shadow: 0 12px 30px rgba(0,0,0,0.06); }
                .provider-card.active { border-color: var(--portal-accent); background: var(--portal-accent-soft); border-width: 2px; }
                
                .provider-icon-box { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .provider-info { flex: 1; }
                .provider-info h6 { margin: 0 0 4px; font-size: 1rem; font-weight: 700; color: var(--portal-text); }
                .provider-info p { margin: 0; font-size: 0.85rem; color: var(--portal-text-muted); line-height: 1.4; }
                .provider-arrow { color: var(--portal-text-muted); opacity: 0; transition: all 0.3s; transform: translateX(-10px); }
                .provider-card:hover .provider-arrow { opacity: 1; transform: translateX(0); color: var(--portal-accent); }

                .code-block { 
                    margin: 1.5rem 0; border-radius: 12px; overflow: hidden; 
                    background: var(--portal-code-bg); 
                    border: 1px solid ${theme === 'dark' ? '#1e2533' : '#1e1b4b'}; 
                    box-shadow: 0 5px 20px rgba(0,0,0,0.15); 
                }
                .code-block pre { padding: 1.5rem; margin: 0; font-family: 'JetBrains Mono', monospace; font-size: 0.95rem; color: #cbd5e1; overflow-x: auto; line-height: 1.6; }

                .doc-list-numbered { padding-left: 1.5rem; margin-bottom: 2rem; color: var(--portal-text-muted); }
                .doc-list-numbered li { margin-bottom: 1.25rem; padding-left: 0.75rem; line-height: 1.6; }

                .theme-toggle-btn, .doc-close-btn {
                    width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
                    background: transparent; border: 1px solid var(--portal-border); color: var(--portal-text-muted); cursor: pointer;
                    transition: all 0.3s;
                }
                .theme-toggle-btn:hover { background: var(--portal-accent); color: white; transform: rotate(15deg); }

                .fade-in { animation: fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <aside className="doc-sidebar">
                <div className="doc-sidebar-header">
                    <div className="doc-logo">
                        <div className="logo-icon-box"><Book size={18} color="#fff" /></div>
                        <span>Documentation</span>
                    </div>
                    <div className="header-actions">
                        <button className="theme-toggle-btn" onClick={toggleTheme} title="Toggle Theme">
                            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                        </button>
                        {onClose && <button className="doc-close-btn" onClick={onClose}><X size={16} /></button>}
                    </div>
                </div>

                <nav className="doc-nav">
                    <div className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`} onClick={() => setActiveSection('overview')}>
                        <div className="doc-nav-item-content"><Globe size={16} /> Overview</div>
                    </div>
                    <div className={`nav-item ${activeSection === 'get-setup' ? 'active' : ''}`} onClick={() => setActiveSection('get-setup')}>
                        <div className="doc-nav-item-content"><Zap size={16} /> Get set up</div>
                    </div>
                    <div className={`nav-item ${activeSection === 'creds-mgmt' ? 'active' : ''}`} onClick={() => setActiveSection('creds-mgmt')}>
                        <div className="doc-nav-item-content"><Key size={16} /> Cloud Credentials</div>
                    </div>

                    <div style={{ padding: '1.75rem 1rem 0.5rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--portal-text-muted)', letterSpacing: '0.15em', opacity: 0.6 }}>Source Setup</div>

                    <div className="nav-group">
                        <div className={`nav-item ${['configure-source', 'source-onprem', 'source-rds', 'source-cloudsql', 'source-azure'].includes(activeSection) ? 'active' : ''}`} onClick={() => toggleNav('source')}>
                            <div className="doc-nav-item-content"><Server size={16} /> Configure Source</div>
                            <ChevronDown size={14} style={{ transform: expandedNav.source ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </div>
                        <div className={`nav-sub-items ${expandedNav.source ? 'expanded' : 'collapsed'}`}>
                            <div className={`sub-nav-item ${activeSection === 'configure-source' ? 'active' : ''}`} onClick={() => setActiveSection('configure-source')}>Overview</div>
                            <div className={`sub-nav-item ${activeSection === 'source-onprem' ? 'active' : ''}`} onClick={() => setActiveSection('source-onprem')}>On-premise / VM</div>
                            <div className={`sub-nav-item ${activeSection === 'source-rds' ? 'active' : ''}`} onClick={() => setActiveSection('source-rds')}>Amazon RDS</div>
                            <div className={`sub-nav-item ${activeSection === 'source-cloudsql' ? 'active' : ''}`} onClick={() => setActiveSection('source-cloudsql')}>Google Cloud SQL</div>
                            <div className={`sub-nav-item ${activeSection === 'source-azure' ? 'active' : ''}`} onClick={() => setActiveSection('source-azure')}>Azure Database</div>
                        </div>
                    </div>

                    <div className={`nav-item ${activeSection === 'create-connection' ? 'active' : ''}`} onClick={() => setActiveSection('create-connection')}>
                        <div className="doc-nav-item-content"><Database size={16} /> Source Profiles</div>
                    </div>
                    <div className={`nav-item ${activeSection === 'create-dest-connection' ? 'active' : ''}`} onClick={() => setActiveSection('create-dest-connection')}>
                        <div className="doc-nav-item-content"><Shield size={16} /> Target Profiles</div>
                    </div>

                    <div style={{ padding: '1.75rem 1rem 0.5rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--portal-text-muted)', letterSpacing: '0.15em', opacity: 0.6 }}>Infrastructure</div>

                    <div className="nav-group">
                        <div className={`nav-item ${['conn-overview', 'conn-ip', 'conn-ssh', 'conn-vpn'].includes(activeSection) ? 'active' : ''}`} onClick={() => toggleNav('connectivity')}>
                            <div className="doc-nav-item-content"><Wifi size={16} /> Connectivity</div>
                            <ChevronDown size={14} style={{ transform: expandedNav.connectivity ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </div>
                        <div className={`nav-sub-items ${expandedNav.connectivity ? 'expanded' : 'collapsed'}`}>
                            <div className={`sub-nav-item ${activeSection === 'conn-overview' ? 'active' : ''}`} onClick={() => setActiveSection('conn-overview')}>Overview</div>
                            <div className={`sub-nav-item ${activeSection === 'conn-ip' ? 'active' : ''}`} onClick={() => setActiveSection('conn-ip')}>IP Allowlists</div>
                            <div className={`sub-nav-item ${activeSection === 'conn-ssh' ? 'active' : ''}`} onClick={() => setActiveSection('conn-ssh')}>Managed SSH</div>
                            <div className={`sub-nav-item ${activeSection === 'conn-vpn' ? 'active' : ''}`} onClick={() => setActiveSection('conn-vpn')}>VPN Bridge</div>
                        </div>
                    </div>

                    <div style={{ padding: '1.75rem 1rem 0.5rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--portal-text-muted)', letterSpacing: '0.15em', opacity: 0.6 }}>Operations</div>

                    <div className="nav-group">
                        <div className={`nav-item ${['mig-overview', 'mig-new', 'mig-existing'].includes(activeSection) ? 'active' : ''}`} onClick={() => toggleNav('migration')}>
                            <div className="doc-nav-item-content"><Activity size={16} /> Migration Jobs</div>
                            <ChevronDown size={14} style={{ transform: expandedNav.migration ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </div>
                        <div className={`nav-sub-items ${expandedNav.migration ? 'expanded' : 'collapsed'}`}>
                            <div className={`sub-nav-item ${activeSection === 'mig-overview' ? 'active' : ''}`} onClick={() => setActiveSection('mig-overview')}>Overview</div>
                            <div className={`sub-nav-item ${activeSection === 'mig-new' ? 'active' : ''}`} onClick={() => setActiveSection('mig-new')}>New Target Setup</div>
                            <div className={`sub-nav-item ${activeSection === 'mig-existing' ? 'active' : ''}`} onClick={() => setActiveSection('mig-existing')}>Existing Target Setup</div>
                        </div>
                    </div>
                </nav>
            </aside>

            <main className="doc-main-content">
                <div className="doc-content-wrapper" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    {renderContent()}
                </div>
            </main>
        </div>
    );
};

export default DocumentationPage;
